<template>
  <section class="page" data-testid="page-data">
    <header class="page-header">
      <div><h1>导入</h1><p>上传 NTQQ 数据库，解析会话并构建人设/RAG 数据。</p></div>
      <div class="actions">
        <button class="button" :disabled="!parsePayload && !buildPayload" @click="copyCurrentView">复制导入视图</button>
        <button class="button primary" :disabled="loading" @click="loadPersonas">刷新人设</button>
      </div>
    </header>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="split">
      <div>
        <div class="panel">
          <h2>上传与解析</h2>
          <input type="file" accept=".db,.zip" :disabled="loading" @change="upload" />
          <div class="toolbar">
            <input v-model="filename" placeholder="上传后的文件名" />
            <button class="button primary" :disabled="loading || !filename.trim()" @click="parse">{{ parsing ? "解析中..." : "解析" }}</button>
          </div>
          <p v-if="uploadError" class="error-text">{{ uploadError }}</p>
          <p v-if="parseError" class="error-text">{{ parseError }}</p>
          <div v-if="parsePayload" class="metrics-grid">
            <MetricCard label="消息总量" :value="parsePayload.total_messages || 0" />
            <MetricCard label="会话数" :value="peerRows.length" />
            <MetricCard label="消息表" :value="(parsePayload.msg_tables || []).length" />
            <MetricCard label="发送者候选" :value="importMetrics.candidates" />
            <MetricCard label="最大会话" :value="importMetrics.topPeerMessages" />
          </div>
          <DataTable v-if="peerRows.length" :columns="peerColumns" :rows="peerRows" row-key="peer_id">
            <template #sender_candidates="{ row }">
              <div class="candidate-list">
                <button
                  v-for="item in senderCandidates(row)"
                  :key="item.sender_id"
                  class="button small"
                  :class="{ primary: buildForm.peer_id === row.peer_id && buildForm.target_user === item.sender_id }"
                  @click="selectPeerTarget(row, item.sender_id)"
                >
                  {{ item.sender_id }} · {{ item.message_count }}
                </button>
                <span v-if="!senderCandidates(row).length" class="muted">-</span>
              </div>
            </template>
            <template #actions="{ row }">
              <div class="row-actions">
                <button class="button small" @click="selectPeer(row)">选择</button>
                <button class="button small" @click="copyJson(row, '会话候选已复制')">复制</button>
              </div>
            </template>
          </DataTable>
          <div v-else-if="parsePayload" class="empty">没有找到可导入的会话。请确认上传的是 NTQQ 数据库。</div>
          <div v-if="parsePayload" class="material-advice" :class="parseQuality.level">
            <strong>{{ parseQuality.title }}</strong>
            <span>{{ parseQuality.detail }}</span>
          </div>
          <pre>{{ parseResult || "上传 .db 或 .zip 后解析，会在这里看到可选择的会话。" }}</pre>
        </div>
      </div>
      <div class="panel">
        <h2>构建人设</h2>
        <div class="form-grid">
          <FormField label="Peer ID"><input v-model="buildForm.peer_id" /></FormField>
          <FormField label="目标用户">
            <select v-if="selectedPeerSenderCandidates.length" v-model="targetUserSelection" @change="applyTargetUserSelection">
              <option value="">选择发送者</option>
              <option v-for="item in selectedPeerSenderCandidates" :key="item.sender_id" :value="item.sender_id">
                {{ item.sender_id }} · {{ item.message_count }}
              </option>
              <option value="__custom">手动输入</option>
            </select>
            <input v-else v-model="buildForm.target_user" />
          </FormField>
          <FormField v-if="targetUserSelection === '__custom'" label="目标用户 QQ / UID"><input v-model="buildForm.target_user" /></FormField>
          <FormField label="目标人设">
            <select v-model="personaSelection" @change="applyPersonaSelection">
              <option value="__new">新建人设</option>
              <option v-for="name in personas" :key="name" :value="name">更新 {{ name }}</option>
              <option value="__custom">手动输入</option>
            </select>
          </FormField>
          <FormField v-if="personaSelection === '__new' || personaSelection === '__custom'" label="人设名">
            <input v-model="buildForm.persona_name" @input="syncPersonaSelectionFromName" />
          </FormField>
          <FormField v-else label="人设名"><input :value="buildForm.persona_name" disabled /></FormField>
        </div>
        <label v-if="personaExists" class="check-row"><input v-model="buildForm.allow_update" type="checkbox" /> 确认更新已存在人设</label>
        <p v-if="personaExists && !buildForm.allow_update" class="error-text">该人设已存在，需确认更新后继续。</p>
        <button class="button primary" :disabled="loading || !canBuild" @click="build">{{ building ? "构建中..." : "构建" }}</button>
        <div class="material-advice" :class="buildQuality.level">
          <strong>{{ buildQuality.title }}</strong>
          <span>{{ buildQuality.detail }}</span>
        </div>
        <p v-if="buildError" class="error-text">{{ buildError }}</p>
        <div v-if="buildPayload" class="result-box" :class="buildPayload.status">
          <h3>{{ buildPayload.persona_saved ? "人设已保存" : "构建未完成" }}</h3>
          <p>
            导入 {{ buildPayload.messages_imported || 0 }} 条消息，
            命中目标用户 {{ buildPayload.target_messages || 0 }} 条，
            当前记忆 {{ buildPayload.memory_count || 0 }} 条。
          </p>
          <div v-if="buildPayload.warnings?.length" class="chips">
            <span v-for="item in buildPayload.warnings" :key="item">{{ item }}</span>
          </div>
          <div class="actions" v-if="buildPayload.persona_saved">
            <RouterLink class="button small" :to="`/personas/${encodeURIComponent(buildPayload.persona_name)}`">查看人设</RouterLink>
            <RouterLink class="button small" to="/memory">检索记忆</RouterLink>
            <RouterLink class="button small" to="/debug">模拟调试</RouterLink>
          </div>
        </div>
        <pre>{{ buildResult || "选择 Peer、目标用户和目标人设后开始构建。" }}</pre>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>会话详情</h2>
        <div v-if="selectedPeer" class="detail-panel">
          <div class="runtime-grid compact">
            <div><span>Peer</span><strong>{{ selectedPeer.peer_id }}</strong></div>
            <div><span>消息</span><strong>{{ selectedPeer.message_count }}</strong></div>
            <div><span>候选</span><strong>{{ senderCandidates(selectedPeer).length }}</strong></div>
            <div><span>目标用户</span><strong>{{ buildForm.target_user || "-" }}</strong></div>
          </div>
          <div class="candidate-list">
            <button
              v-for="item in senderCandidates(selectedPeer)"
              :key="item.sender_id"
              class="button small"
              :class="{ primary: buildForm.target_user === item.sender_id }"
              @click="selectPeerTarget(selectedPeer, item.sender_id)"
            >
              {{ item.sender_id }} · {{ item.message_count }}
            </button>
          </div>
          <pre>{{ JSON.stringify(selectedPeer, null, 2) }}</pre>
        </div>
        <div v-else class="empty">解析后选择一个会话，查看发送者候选和构建目标。</div>
      </div>
      <div class="panel">
        <h2>导入诊断</h2>
        <div class="diagnostic-list">
          <div v-for="item in diagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
            <strong>{{ item.label }}</strong>
            <span>{{ item.detail }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { RouterLink } from "vue-router";
import { api, postJson } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const filename = ref("");
const parseResult = ref("");
const buildResult = ref("");
const parsePayload = ref<any | null>(null);
const buildPayload = ref<any | null>(null);
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择导入数据");
const uploading = ref(false);
const parsing = ref(false);
const building = ref(false);
const uploadError = ref("");
const parseError = ref("");
const buildError = ref("");
const loading = computed(() => uploading.value || parsing.value || building.value);
const buildForm = reactive({ peer_id: "", persona_name: "", target_user: "", allow_update: false });
type SenderCandidate = { sender_id: string; message_count: number };
type PeerRow = { peer_id: string; message_count: number; sender_candidates?: SenderCandidate[] };
const personas = ref<string[]>([]);
const personaSelection = ref("__new");
const targetUserSelection = ref("");
const peerColumns = [
  { key: "peer_id", label: "Peer ID" },
  { key: "message_count", label: "消息" },
  { key: "sender_candidates", label: "发送者候选" },
  { key: "actions", label: "操作" }
];
const peerRows = computed<PeerRow[]>(() => parsePayload.value?.peers_list || []);
const selectedPeer = computed(() => peerRows.value.find((row) => row.peer_id === buildForm.peer_id));
const selectedPeerSenderCandidates = computed(() => senderCandidates(selectedPeer.value));
const personaExists = computed(() => Boolean(buildForm.persona_name.trim() && personas.value.includes(buildForm.persona_name.trim())));
const importMetrics = computed(() => {
  const candidates = peerRows.value.reduce((total, row) => total + senderCandidates(row).length, 0);
  const topPeerMessages = peerRows.value.reduce((max, row) => Math.max(max, Number(row.message_count || 0)), 0);
  return { candidates, topPeerMessages };
});
const canBuild = computed(() => (
  filename.value.trim()
  && buildForm.peer_id
  && buildForm.persona_name.trim()
  && buildForm.target_user.trim()
  && (!personaExists.value || buildForm.allow_update)
));
const parseQuality = computed(() => {
  if (!parsePayload.value) return { level: "warning", title: "等待解析", detail: "上传数据库后解析出会话和发送者候选。" };
  if (!peerRows.value.length) return { level: "warning", title: "无可用会话", detail: "没有解析到可用于构建人设的会话。" };
  if (!importMetrics.value.candidates) return { level: "warning", title: "缺少发送者候选", detail: "需要能识别目标用户发送者，才能构建可信人设。" };
  return { level: "ok", title: "解析结果可用", detail: `${peerRows.value.length} 个会话、${importMetrics.value.candidates} 个发送者候选可用于构建。` };
});
const buildQuality = computed(() => {
  if (!filename.value.trim()) return { level: "warning", title: "缺少文件", detail: "先上传或填写上传后的文件名。" };
  if (!buildForm.peer_id) return { level: "warning", title: "缺少会话", detail: "选择一个 Peer 作为人设构建来源。" };
  if (!buildForm.target_user.trim()) return { level: "warning", title: "缺少目标用户", detail: "选择或输入要人格化学习的 QQ/UID。" };
  if (!buildForm.persona_name.trim()) return { level: "warning", title: "缺少人设名", detail: "为这次导入命名，后续可在调试和记忆页追踪。" };
  if (personaExists.value && !buildForm.allow_update) return { level: "warning", title: "需要确认更新", detail: "该人设已存在，勾选确认后才会覆盖草稿。" };
  return { level: "ok", title: "构建参数可用", detail: "会话、目标用户和人设名都已就绪。" };
});
const diagnostics = computed(() => [
  {
    label: "数据规模",
    detail: parsePayload.value ? `${parsePayload.value.total_messages || 0} 条消息，${peerRows.value.length} 个会话` : "还没有解析结果",
    level: parsePayload.value ? "ok" : "warning"
  },
  {
    label: "目标命中",
    detail: selectedPeer.value
      ? `当前会话最大候选 ${Math.max(0, ...senderCandidates(selectedPeer.value).map((item) => item.message_count))} 条消息`
      : "选择会话后可评估目标用户样本量",
    level: selectedPeer.value ? "ok" : "warning"
  },
  {
    label: "构建结果",
    detail: buildPayload.value
      ? `${buildPayload.value.status || "unknown"}，导入 ${buildPayload.value.messages_imported || 0} 条，目标 ${buildPayload.value.target_messages || 0} 条`
      : "还没有执行人设构建",
    level: buildPayload.value?.persona_saved ? "ok" : "warning"
  }
]);

function senderCandidates(row?: PeerRow) {
  return row?.sender_candidates || [];
}

async function loadPersonas() {
  buildError.value = "";
  try {
    const payload = await api<{ personas: string[] }>("/api/personas");
    personas.value = payload.personas || [];
    syncPersonaSelectionFromName();
    actionResult.value = `已加载 ${personas.value.length} 个人设`;
  } catch (err) {
    buildError.value = err instanceof Error ? err.message : "加载人设失败";
    toast.push(buildError.value, "error");
  }
}

async function copyCurrentView() {
  await copyJson({
    filename: filename.value,
    parse: parsePayload.value,
    build: buildPayload.value,
    selectedPeer: selectedPeer.value,
    form: { ...buildForm },
    diagnostics: diagnostics.value
  }, "导入视图已复制");
}

async function upload(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  const fd = new FormData();
  fd.append("file", file);
  uploading.value = true;
  uploadError.value = "";
  parseError.value = "";
  buildError.value = "";
  actionResult.value = "";
  try {
    const payload = await api<any>("/api/upload", { method: "POST", body: fd });
    filename.value = payload.filename || file.name;
    parsePayload.value = null;
    buildPayload.value = null;
    resetBuildTarget();
    parseResult.value = JSON.stringify(payload, null, 2);
    actionResult.value = `已上传 ${filename.value}`;
    toast.push("上传完成");
  } catch (err) {
    uploadError.value = err instanceof Error ? err.message : "上传失败";
    toast.push(uploadError.value, "error");
  } finally {
    uploading.value = false;
    (event.target as HTMLInputElement).value = "";
  }
}
async function parse() {
  parsing.value = true;
  parseError.value = "";
  buildError.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>("/api/parse", { filename: filename.value });
    parsePayload.value = payload;
    buildPayload.value = null;
    buildResult.value = "";
    parseResult.value = JSON.stringify(payload, null, 2);
    resetBuildTarget();
    actionResult.value = `解析完成：${payload.total_messages || 0} 条消息`;
  } catch (err) {
    parseError.value = err instanceof Error ? err.message : "解析失败";
    toast.push(parseError.value, "error");
  } finally {
    parsing.value = false;
  }
}
function selectPeer(row: PeerRow) {
  buildForm.peer_id = row.peer_id;
  targetUserSelection.value = "";
  buildForm.target_user = "";
  actionResult.value = `已选择会话 ${row.peer_id}`;
  const candidates = senderCandidates(row);
  if (candidates.length === 1) {
    selectPeerTarget(row, candidates[0].sender_id);
  }
}
function selectPeerTarget(row: PeerRow, senderId: string) {
  buildForm.peer_id = row.peer_id;
  buildForm.target_user = senderId;
  targetUserSelection.value = senderId;
  actionResult.value = `已选择目标用户 ${senderId}`;
}
function applyTargetUserSelection() {
  if (targetUserSelection.value === "__custom") return;
  buildForm.target_user = targetUserSelection.value;
}
function applyPersonaSelection() {
  if (personaSelection.value === "__custom") return;
  if (personaSelection.value === "__new") {
    if (personaExists.value) buildForm.persona_name = "";
    buildForm.allow_update = false;
    return;
  }
  buildForm.persona_name = personaSelection.value;
  buildForm.allow_update = false;
}
function syncPersonaSelectionFromName() {
  const name = buildForm.persona_name.trim();
  if (!name) {
    personaSelection.value = "__new";
  } else if (personas.value.includes(name)) {
    personaSelection.value = name;
  } else if (personaSelection.value !== "__custom") {
    personaSelection.value = "__new";
  }
  if (!personaExists.value) buildForm.allow_update = false;
}
function resetBuildTarget() {
  buildForm.peer_id = "";
  buildForm.target_user = "";
  targetUserSelection.value = "";
}
async function build() {
  building.value = true;
  buildError.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>("/api/build-persona", { filename: filename.value, ...buildForm });
    buildPayload.value = payload;
    buildResult.value = JSON.stringify(payload, null, 2);
    actionResult.value = payload.persona_saved ? `人设 ${payload.persona_name} 已保存` : "构建未完成，请查看提示";
    toast.push(payload.persona_saved ? "人设已保存" : "构建未完成，请查看提示", payload.persona_saved ? "success" : "error");
    if (payload.persona_saved) {
      try {
        await loadPersonas();
      } catch (err) {
        buildError.value = err instanceof Error ? err.message : "刷新人设列表失败";
      }
    }
  } catch (err) {
    buildError.value = err instanceof Error ? err.message : "构建失败";
    toast.push(buildError.value, "error");
  } finally {
    building.value = false;
  }
}

onMounted(() => {
  loadPersonas();
});
</script>

<style scoped>
.check-row { display: flex; align-items: center; gap: 8px; color: var(--muted); margin: 8px 0 14px; }
.candidate-list { display: flex; flex-wrap: wrap; gap: 6px; min-width: 220px; }
.muted { color: var(--muted); }
.result-box { border: 1px solid var(--border); border-radius: 8px; padding: 14px; margin: 14px 0; background: var(--panel-2); }
.result-box.completed { border-color: #386f47; }
.result-box.degraded { border-color: #78612d; }
.result-box h3 { margin: 0 0 8px; font-size: 16px; }
</style>
